import React, { useState, useEffect } from "react";
import { Check, ArrowRight } from "lucide-react";
import { CONFIG } from "../config";

function getEasternDateString() {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "long",
    day: "numeric"
  });
  return formatter.format(now).toUpperCase();
}

export default function ProcessSection() {
  const [letterDate, setLetterDate] = useState(getEasternDateString());

  useEffect(() => {
    const interval = setInterval(() => {
      setLetterDate(getEasternDateString());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const checkItems = [
    "PROVE THE OWNERSHIP.",
    "PROVE THE BALANCE.",
    "PROVE THE ACCOUNT HISTORY.",
    "PROVE THE ASSIGNMENT TRAIL.",
    "PROVE THE CREDIT REPORTING.",
    "PROVE THE AUTHORITY TO COLLECT."
  ];

  return (
    <>
      <section id="the-process" className="bg-white pt-12 pb-14 md:pt-16 md:pb-16 border-b border-brand-border" data-testid="process-section">
        <div className="max-w-[1120px] mx-auto px-4 md:px-6">

          {/* Main Headline */}
          <h2 className="text-center font-display font-black uppercase text-brand-dark tracking-tight leading-[1.05] mb-4 max-w-[1000px] mx-auto text-3xl sm:text-5xl lg:text-[52px]">
            <span className="block">YOU ARE <span className="text-brand-red">NOT ALONE</span>.</span>
          </h2>

          {/* Subhead */}
          <p className="text-justify font-display font-bold text-brand-dark/90 leading-snug max-w-[820px] mx-auto mb-4 text-lg sm:text-xl">
            <span className="font-black">MORE THAN 70 MILLION AMERICANS HAVE COLLECTIONS BEING REPORTED!</span> THAT'S 35% OF THE U.S. POPULATION! Medical emergencies happen. Jobs disappear. Businesses fail. Families split up. People get sick. People die. Financial hardship happens. A collection does not mean you are powerless.
          </p>

          <div className="max-w-[820px] mx-auto text-center mb-2">
            <p className="font-display font-black uppercase text-brand-dark text-base md:text-xl tracking-tight leading-snug bg-white border-2 border-brand-dark rounded-lg py-3 px-5">
              <span className="block lg:whitespace-nowrap">THE <span className="text-brand-red">DEBT COLLECTOR KILL SWITCH</span> IS DESIGNED TO HIT COLLECTORS</span>
              <span className="block lg:whitespace-nowrap">AND BUREAUS <span className="text-brand-red">FAST</span> WITH <span className="text-brand-red">POWERFUL</span> LEGAL <span className="text-brand-red">DEMANDS</span> THEY CAN&rsquo;T IGNORE.</span>
            </p>
          </div>

        </div>
      </section>

      <section id="how-the-kill-switch-works" className="bg-white pt-12 pb-14 md:pt-16 md:pb-16 border-b border-brand-border" data-testid="process-section-2">
        <div className="max-w-[1120px] mx-auto px-4 md:px-6">

          <div>

            <h3 className="text-center font-display font-black uppercase text-brand-dark tracking-tight leading-[1.05] mb-4 max-w-[1020px] mx-auto text-3xl sm:text-5xl lg:text-[52px]">
              <span className="block lg:whitespace-nowrap">THEY WANT THE <span className="text-brand-red">FULL BALANCE</span>.</span>
              <span className="block lg:whitespace-nowrap mt-1">THE KILL SWITCH DEMANDS THE <span className="text-brand-red">FULL PROOF</span>!</span>
            </h3>

            <p className="text-justify font-sans font-semibold text-brand-dark/90 text-sm md:text-base leading-relaxed max-w-[820px] mx-auto mb-4">
              &ldquo;<span className="text-brand-red">WAIT A MINUTE!</span> ARE YOU TRYING TO COLLECT THIS <span className="text-brand-red">ALLEGED DEBT</span> FROM ME, WITHOUT PROVIDING ME PROOF YOU HAVE THE <span className="text-brand-red">LEGAL AUTHORITY</span>?&rdquo;
            </p>

            <p className="text-justify font-sans font-semibold text-brand-dark/90 text-sm md:text-base leading-relaxed max-w-[820px] mx-auto mb-4">
              &ldquo;<span className="text-brand-red">I DON&rsquo;T THINK SO!</span> DO YOU HAVE THE COMPLETE CHAIN OF TITLE, FORWARD FLOW AGREEMENT, BILL OF SALE, ASSIGNMENT DOCUMENTS, AND <span className="text-brand-red">MY OTHER 68 VALIDATION DEMANDS</span> TO SUPPORT YOUR CLAIM TO COLLECT?&rdquo;
            </p>

            <p className="text-justify font-sans font-semibold text-brand-dark/90 text-sm md:text-base leading-relaxed max-w-[820px] mx-auto mb-4">
              &ldquo;I AM FORMALLY DISPUTING THIS <span className="text-brand-red">ALLEGED DEBT</span> AND <span className="text-brand-red">DEMANDING ACCOUNT LEVEL VALIDATION</span>. UNDER THE FDCPA, ****, AND ****, A DEBT COLLECTOR MUST CEASE COLLECTION OF THE DISPUTED DEBT UNTIL THE VERIFICATION REQUIRED BY LAW IS PROVIDED.&rdquo;
            </p>

            <p className="text-justify font-sans font-semibold text-brand-dark/90 text-sm md:text-base leading-relaxed max-w-[820px] mx-auto mb-8">
              &ldquo;NOW ADDRESS MY 68 ACCOUNT LEVEL VALIDATION DEMANDS, IN FULL! AND DO NOT SEND ME A STACK OF STATEMENTS OR A DISCLOSURE; I NEED FULL ACCOUNT LEVEL VALIDATION WITH THE EXACT RESPONSE TO EACH OF MY 68 DEMANDS FOR YOU TO PROVE YOUR LEGAL AUTHORITY! ANYTHING LESS I WILL BE FILING A CFPB COMPLAINT AND POTENTIALLY SEEK DAMAGES.&rdquo;
            </p>

            <div className="max-w-[820px] mx-auto text-center mb-8">
              <p className="font-display font-black uppercase text-brand-dark text-base md:text-xl tracking-tight leading-snug bg-white border-2 border-brand-dark rounded-lg py-3 px-5">
                <span className="block text-brand-red">IN THE NEXT 30 MINUTES!</span>
                <span className="block lg:whitespace-nowrap mt-1">PUT THE DEBT COLLECTOR INTO THE FEDERAL VALIDATION PROCESS</span>
                <span className="block mt-1"><span className="marker-highlight">THEN WATCH WHAT MAGICALLY HAPPENS!</span></span>
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">

              {/* LEFT: Live Validation Letter Document (7 columns ~ 58%) */}
              <div id="debt-validation-letter" className="lg:col-span-7">
                <div className="bg-white border-[3px] border-brand-dark rounded-xl shadow-md p-2">
                  <div className="border border-gray-300 rounded-lg p-6 md:p-8">

                    {/* Date */}
                    <div className="text-right mb-5">
                      <span className="font-mono text-xs md:text-sm">
                        <span className="text-red-800 font-bold">DATE:</span>{" "}
                        <span className="text-black font-bold" data-testid="letter-live-date">{letterDate}</span>
                      </span>
                    </div>

                    {/* Header */}
                    <div className="text-center mb-5">
                      <p className="font-display font-black uppercase text-red-800 text-sm sm:text-lg md:text-xl leading-tight tracking-tight lg:whitespace-nowrap">
                        PROVE YOU HAVE THE LEGAL AUTHORITY TO COLLECT!
                      </p>
                    </div>
                    <hr className="border-gray-300 mb-5" />

                    {/* Address / Account */}
                    <div className="mb-5 font-mono text-xs md:text-sm space-y-1.5">
                      <p><span className="text-red-800 font-bold">TO:</span> <span className="text-black font-semibold">PORTFOLIO RECOVERY</span></p>
                      <p><span className="text-red-800 font-bold">RE:</span> <span className="text-black font-semibold">ACCOUNT VALIDATION DEMAND</span></p>
                    </div>
                    <hr className="border-gray-300 mb-5" />

                    {/* Main Demand Statement */}
                    <p className="font-mono font-bold text-black text-sm md:text-base leading-relaxed text-left mb-5">
                      PROVIDE COMPLETE ACCOUNT-LEVEL VALIDATION AND DOCUMENTATION TO PROVE YOUR <span className="text-red-800 font-bold">LEGAL AUTHORITY TO COLLECT</span> THIS ALLEGED DEBT.
                    </p>

                    {/* Sample 68-Demand Content */}
                    <div className="space-y-3 mb-5">
                      <div className="flex items-start gap-2">
                        <div className="w-4 h-4 mt-0.5 bg-brand-success text-white rounded-full flex items-center justify-center shrink-0">
                          <Check className="w-2.5 h-2.5 stroke-[4]" aria-hidden="true" />
                        </div>
                        <p className="font-mono text-xs md:text-sm text-black leading-relaxed text-left truncate">
                          <span className="text-red-800 font-bold">18.</span> All amendments, change-in-terms notices, interest-rate..
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-4 h-4 mt-0.5 bg-brand-success text-white rounded-full flex items-center justify-center shrink-0">
                          <Check className="w-2.5 h-2.5 stroke-[4]" aria-hidden="true" />
                        </div>
                        <p className="font-mono text-xs md:text-sm text-black leading-relaxed text-left truncate">
                          <span className="text-red-800 font-bold">30.</span> If your office relies on a generic bill of sale, prov...
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-4 h-4 mt-0.5 bg-brand-success text-white rounded-full flex items-center justify-center shrink-0">
                          <Check className="w-2.5 h-2.5 stroke-[4]" aria-hidden="true" />
                        </div>
                        <p className="font-mono text-xs md:text-sm text-black leading-relaxed text-left truncate">
                          <span className="text-red-800 font-bold">68.</span> Confirmation that if this alleged debt cannot be full...
                        </p>
                      </div>
                    </div>
                    <hr className="border-gray-300 mb-5" />

                    {/* Insufficient Validation Statement */}
                    <p className="text-center font-mono font-bold uppercase text-xs sm:text-sm md:text-base leading-snug mb-5">
                      <span className="text-red-800">INSUFFICIENT VALIDATION</span> <span className="text-black">IS NOT ACCEPTED AS ACCOUNT-LEVEL PROOF</span>
                    </p>
                    <hr className="border-gray-300 mb-5" />

                    {/* Final Legal Notice */}
                    <p className="font-mono text-[10px] md:text-xs text-black leading-relaxed text-justify">
                      <span className="text-red-800 font-bold uppercase">NOTICE:</span> If this disputed debt cannot be verified as required by **** and ****, YOU MUST <span className="text-red-800 font-bold">cease collection</span> of the disputed debt until the verification required by law is provided. If this account is being furnished to a consumer reporting agency, disputed information must be handled in accordance with applicable FCRA investigation...
                    </p>

                  </div>
                </div>
              </div>

              {/* RIGHT: Checklist & Transition Statement (5 columns ~ 42%) */}
              <div className="lg:col-span-5 flex flex-col justify-start">
                <h3 className="font-display font-black uppercase text-xl sm:text-2xl text-brand-dark mb-4 text-left">
                  <span className="block whitespace-nowrap">HEY DEBT COLLECTOR! WHO ARE YOU?</span>
                  <span className="block whitespace-nowrap"><span className="marker-highlight">DID I EVER AGREE TO DO BUSINESS</span></span>
                  <span className="block whitespace-nowrap"><span className="marker-highlight">WITH YOUR COMPANY?</span> <span className="text-brand-red">PROVE IT!</span></span>
                </h3>

                <div className="grid grid-cols-1 gap-3 mb-6">
                  {checkItems.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-card border-2 border-brand-dark px-5 py-2.5 rounded-lg shadow-sm">
                      <div className="w-5 h-5 bg-brand-success text-white rounded-full flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3 stroke-[4]" />
                      </div>
                      <span className="font-display font-black text-xs md:text-sm text-brand-dark tracking-tight">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>

                <p className="font-display font-bold text-brand-dark/95 leading-snug text-base sm:text-lg lg:text-xl border-l-4 border-brand-red pl-4 py-1">
                  <span className="block">TAKE ACTION AND DEMAND VALIDATION!</span>
                  <span className="block mt-1"><span className="marker-highlight">WATCH HOW FAST THEY FOLD!</span></span>
                </p>

                <div className="mt-6 text-center">
                  <a
                    href={CONFIG.checkoutUrl}
                    className="w-full flex items-center justify-center py-4 px-4 bg-brand-cta hover:bg-brand-cta-hover text-white rounded-lg font-sans font-extrabold uppercase text-xs sm:text-sm md:text-base tracking-wide whitespace-nowrap border-b-4 border-brand-dark/30 hover:border-b-2 active:border-b-0 hover:translate-y-[2px] active:translate-y-[4px] transition-all duration-150 cursor-pointer shadow-md"
                    data-testid="process-signup-cta-button"
                  >
                    YES! I&apos;M READY TO REMOVE MY COLLECTIONS
                    <ArrowRight className="w-4 h-4 ml-2 shrink-0" />
                  </a>
                  <p className="font-display font-black text-brand-dark text-sm mt-3">
                    🔒 Secure checkout ⚡ Instant Private Access
                  </p>
                  <p className="text-center text-brand-red font-sans font-bold text-xs mt-2">
                    START REMOVING YOUR COLLECTIONS TODAY!
                  </p>
                </div>
              </div>

            </div>

          </div>

        </div>
      </section>
    </>
  );
}
